/// <reference types="new-relic-browser" preserve="true" />

import { SetTransactionMetadata } from "../common/types";
import { array, create, Infer, object, string, StructError } from "superstruct";

/* eslint-disable no-var */
declare global {
  var NREUM: typeof newrelic;
}
/* eslint-enable no-var */

const ActiveCustomAttributesSchema = object({
  newRelicCustomAttributes: array(string()),
});
type ActiveCustomAttributes = Infer<typeof ActiveCustomAttributesSchema>;

// This key is not renamed from @argonaut/observability-custom_attributes to @rea-argonaut/observability-custom_attributes because its value needs to be shared by app shell and MFE
const STORAGE_KEY = "@argonaut/observability-custom_attributes";

const getDefaultCustomAttribute = (): ActiveCustomAttributes => {
  return {
    newRelicCustomAttributes: [],
  };
};

const getStoredCustomAttributes = (): ActiveCustomAttributes | null => {
  try {
    const storedCustomAttributes = sessionStorage.getItem(STORAGE_KEY);
    return storedCustomAttributes
      ? create(JSON.parse(storedCustomAttributes), ActiveCustomAttributesSchema)
      : null;
  } catch (e) {
    if (e instanceof StructError) {
      console.warn("Failed to parse stored custom attributes", {
        branch: e.branch,
        failures: e.failures(),
        error: e.message,
      });
    }
    return null;
  }
};

const setBrowserTransactionMetadata: SetTransactionMetadata = ({
  newRelicKey,
  value,
}): void => {
  try {
    if (typeof NREUM === "object" && !!sessionStorage) {
      const storedCustomAttributes =
        getStoredCustomAttributes() ?? getDefaultCustomAttribute();

      storedCustomAttributes.newRelicCustomAttributes.push(newRelicKey);

      if (typeof value === "boolean") {
        value = value.toString();
      }

      NREUM.setCustomAttribute(newRelicKey, value);
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(storedCustomAttributes),
      );
    }
  } catch (e) {
    if (e instanceof Error) {
      console.warn("Failed to set browser transaction metadata", {
        error: e.message,
      });
    }
  }
};

export const resetBrowserTransactionMetadata = (): void => {
  try {
    const storedCustomAttributes = getStoredCustomAttributes();
    if (typeof NREUM === "object" && storedCustomAttributes) {
      storedCustomAttributes.newRelicCustomAttributes.forEach((newRelicKey) => {
        // this type conversion is needed as the types definition supplied here by @types/new-relic-browser is outdated. The value can be null
        // https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/setcustomattribute/
        NREUM.setCustomAttribute(newRelicKey, null as unknown as string);
      });
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    if (e instanceof Error) {
      console.warn("Failed to reset browser transaction metadata", {
        error: e.message,
      });
    }
  }
};

export default setBrowserTransactionMetadata;
